import api from '@services/api';
import {
  ShotEvaluation,
  ShotType,
  ApiResponse,
  PaginatedResponse,
} from '@types/index';

/**
 * Shot Management API service
 */
class ShotService {
  /**
   * Get all active shot types
   */
  async getShotTypes(): Promise<ShotType[]> {
    const response = await api.get<ApiResponse<ShotType[]>>('/shots/types');
    return response.data.data!;
  }

  /**
   * Get shot types grouped by difficulty
   */
  async getShotTypesByDifficulty(): Promise<any[]> {
    const response = await api.get<ApiResponse<any[]>>('/shots/types/by-difficulty');
    return response.data.data!;
  }

  /**
   * Create shot evaluation
   */
  async createShotEvaluation(data: {
    player_id: string;
    shot_type_id: string;
    session_date: string;
    session_type: 'live_practice' | 'video_submission';
    total_attempts: number;
    successful_attempts: number;
    failed_attempts: number;
    accuracy_score: number;
    speed_level: 'slow' | 'medium' | 'fast' | 'very_fast';
    speed_score: number;
    evaluator_id?: string;
    video_url?: string;
    notes?: string;
    placements?: Array<{
      grid_zone_id?: string;
      placement_x?: number;
      placement_y?: number;
      was_successful: boolean;
    }>;
  }): Promise<ShotEvaluation> {
    const response = await api.post<ApiResponse<ShotEvaluation>>(
      '/shots/evaluations',
      data
    );
    return response.data.data!;
  }

  /**
   * Get player's shot performance summary
   */
  async getPlayerShotSummary(playerId: string): Promise<any[]> {
    const response = await api.get<ApiResponse<any[]>>(
      `/shots/players/${playerId}/summary`
    );
    return response.data.data!;
  }

  /**
   * Get player's shot performance trends
   */
  async getPlayerShotTrends(
    playerId: string,
    shotTypeId: string,
    days: number = 30
  ): Promise<any[]> {
    const response = await api.get<ApiResponse<any[]>>(
      `/shots/players/${playerId}/trends/${shotTypeId}?days=${days}`
    );
    return response.data.data!;
  }

  /**
   * Get evaluations for a player
   */
  async getPlayerEvaluations(
    playerId: string,
    limit: number = 50,
    offset: number = 0
  ): Promise<ShotEvaluation[]> {
    const response = await api.get<ApiResponse<ShotEvaluation[]>>(
      `/shots/players/${playerId}/evaluations?limit=${limit}&offset=${offset}`
    );
    return response.data.data!;
  }

  /**
   * Validate shot evaluation (coach only)
   */
  async validateEvaluation(
    evaluationId: string,
    isValid: boolean,
    notes?: string
  ): Promise<void> {
    await api.put(`/shots/evaluations/${evaluationId}/validate`, {
      is_valid: isValid,
      notes,
    });
  }

  /**
   * Get pending evaluations for coach
   */
  async getPendingEvaluations(): Promise<ShotEvaluation[]> {
    const response = await api.get<ApiResponse<ShotEvaluation[]>>(
      '/shots/evaluations/pending'
    );
    return response.data.data!;
  }

  /**
   * Get court grid zones
   */
  async getCourtGrids(courtType: 'singles' | 'doubles'): Promise<any[]> {
    const response = await api.get<ApiResponse<any[]>>(
      `/shots/court-grids/${courtType}`
    );
    return response.data.data!;
  }

  /**
   * Get court grids grouped by area
   */
  async getCourtGridsByArea(courtType: 'singles' | 'doubles'): Promise<any[]> {
    const response = await api.get<ApiResponse<any[]>>(
      `/shots/court-grids/${courtType}/by-area`
    );
    return response.data.data!;
  }

  /**
   * Get shot placement heatmap data
   */
  async getShotHeatmapData(
    playerId: string,
    shotTypeId?: string,
    startDate?: string,
    endDate?: string
  ): Promise<any[]> {
    const params = new URLSearchParams();
    if (shotTypeId) params.append('shot_type_id', shotTypeId);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await api.get<ApiResponse<any[]>>(
      `/shots/players/${playerId}/heatmap?${params.toString()}`
    );
    return response.data.data!;
  }

  /**
   * Find zone by coordinates
   */
  async findZoneByCoordinates(
    x: number,
    y: number,
    courtType: 'singles' | 'doubles'
  ): Promise<any> {
    const response = await api.post<ApiResponse<any>>(
      '/shots/court-grids/find-zone',
      { x, y, court_type: courtType }
    );
    return response.data.data!;
  }

  /**
   * Get coach evaluation statistics
   */
  async getCoachEvaluationStats(
    coachId: string,
    days: number = 30
  ): Promise<any> {
    const response = await api.get<ApiResponse<any>>(
      `/shots/coaches/${coachId}/stats?days=${days}`
    );
    return response.data.data!;
  }
}

export default new ShotService();

